import type { ReactNode } from "react";
import { NavLink, useNavigate } from "react-router-dom";

/* ──────────────────────────────────────────────────────────────────────────
 * AppShell — the pure-custom application chrome (no Cloudscape).
 * Renders the wireframe's `.shell > .topbar + .rail + .content` exactly.
 * ────────────────────────────────────────────────────────────────────────── */

interface RailItem {
  label: string;
  to: string;
  end?: boolean;
}

const WORKSPACE: RailItem[] = [
  { label: "Home", to: "/home", end: true },
  { label: "Performance Intake", to: "/performance/intake" },
  { label: "Validations", to: "/performance/runs", end: true },
  { label: "Application Catalog", to: "/catalog/applications" },
  { label: "Analytics", to: "/performance/analytics" },
  { label: "Reports", to: "/performance/reports", end: true },
];

const TEAM_VIEWS: RailItem[] = [
  { label: "Run Evidence", to: "/performance/runs" },
  { label: "Risk Review", to: "/performance/reports" },
  { label: "Workflow Health", to: "/performance/agents" },
];

function RailLink({ item }: { item: RailItem }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) => `rail__item${isActive ? " is-active" : ""}`}
    >
      <span className="rail__dot" />
      {item.label}
    </NavLink>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  return (
    <div className="rpe">
      <div className="shell">
        <div className="topbar">
          <div
            className="brand"
            role="link"
            tabIndex={0}
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/home")}
            onKeyDown={(e) => (e.key === "Enter" ? navigate("/home") : undefined)}
          >
            <span className="brand__mark" />
            <span className="brand__name">lululemon</span>
            <span className="brand__sep">.</span>
            <span className="brand__product">Retail Performance Engineering</span>
          </div>
          <div className="topbar__spacer" />
          <div className="topbar__search">Search validations, apps, teams, reports</div>
          <button
            type="button"
            className="topbar__menu"
            onClick={() => navigate("/settings/user-tokens")}
          >
            Settings
          </button>
          <div className="avatar">PE</div>
        </div>

        <div className="rail">
          <div className="rail__section">Workspace</div>
          {WORKSPACE.map((item) => (
            <RailLink key={item.label} item={item} />
          ))}
          <div className="rail__divider" />
          <div className="rail__section">Team Views</div>
          {TEAM_VIEWS.map((item) => (
            <RailLink key={item.label} item={item} />
          ))}
        </div>

        <div className="content">{children}</div>
      </div>
    </div>
  );
}
